import { ApiResponse } from 'apisauce';

import {
  IIncident,
  IIncidentSnapshot,
  IMngr,
  IMngrSnapshot,
  IMonitor,
  IMonitorSnapshot,
  IServiceHist,
  IServiceHistSnapshot,
} from '../../models';
import { Api } from '../api/api';
import { getGeneralApiProblem } from '../api/api-problem';

import {
  TDeleteServiceHistResult,
  TGetIncidentResult,
  TGetMngrResult,
  TGetMonitorResult,
  TGetServiceHistResult,
  TSaveIncidentResult,
  TSaveMngrResult,
  TSaveMonitorResult,
  TSaveServiceHistResult,
} from './ServiceHistTypes';

/**
 * # ServiceHist Api 서비스
 *
 * 서비스의 설명을 작성하세요.
 *
 * ## 사용방법
 *
 * ```ts
 * const service = new ServiceHistApi();
 *
 * ```
 */
export class ServiceHistApi {
  private api: Api;

  constructor(api: Api) {
    this.api = api;
  }

  async getServiceHist(options?: { useYn?: 'Y' | 'N' }): Promise<TGetServiceHistResult> {
    try {
      const url = '/serviceHist';
      const params = options ?? {};
      const response: ApiResponse<any> = await this.api.apisauce.get(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const serviceHist: IServiceHist[] = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', serviceHist, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async saveServiceHist(newServiceHist: IServiceHistSnapshot): Promise<TSaveServiceHistResult> {
    try {
      const hasSid = !!newServiceHist.serviceHistSid;
      const url = hasSid ? `/serviceHist/${newServiceHist.serviceHistSid}` : '/serviceHist';
      const method = hasSid ? this.api.apisauce.put : this.api.apisauce.post;
      const response: ApiResponse<any> = await method(url, newServiceHist);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const serviceHist: IServiceHist = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', serviceHist, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async deleteServiceHist(serviceHistSid: number): Promise<TDeleteServiceHistResult> {
    try {
      const url = `/serviceHist/${serviceHistSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async getMonitor(options?: { useYn?: 'Y' | 'N' }): Promise<TGetMonitorResult> {
    try {
      const url = '/monitoring';
      const params = options ?? {};
      const response: ApiResponse<any> = await this.api.apisauce.get(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const monitor: IMonitor[] = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', monitor, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async saveMonitor(newMonitor: IMonitorSnapshot): Promise<TSaveMonitorResult> {
    try {
      const hasSid = !!newMonitor.monitorSid;
      const url = hasSid ? `/monitoring/${newMonitor.monitorSid}` : '/monitoring';
      const method = hasSid ? this.api.apisauce.put : this.api.apisauce.post;
      const response: ApiResponse<any> = await method(url, newMonitor);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const monitor: IMonitor = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', monitor, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async deleteMonitor(monitorSid: number): Promise<TDeleteServiceHistResult> {
    try {
      const url = `/monitoring/${monitorSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async getIncident(options?: { useYn?: 'Y' | 'N' }): Promise<TGetIncidentResult> {
    try {
      const url = '/incident';
      const params = options ?? {};
      const response: ApiResponse<any> = await this.api.apisauce.get(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const incident: IIncident[] = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', incident, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async saveIncident(newIncident: IIncidentSnapshot): Promise<TSaveIncidentResult> {
    try {
      const hasSid = !!newIncident.incidentSid;
      const url = hasSid ? `/incident/${newIncident.incidentSid}` : '/incident';
      const method = hasSid ? this.api.apisauce.put : this.api.apisauce.post;
      const response: ApiResponse<any> = await method(url, newIncident);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const incident: IIncident = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', incident, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async deleteIncident(incidentSid: number): Promise<TDeleteServiceHistResult> {
    try {
      const url = `/incident/${incidentSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async getMngr(options?: { useYn?: 'Y' | 'N' }): Promise<TGetMngrResult> {
    try {
      const url = '/mngrInfo';
      const params = options ?? {};
      const response: ApiResponse<any> = await this.api.apisauce.get(url, params);

      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const mngr: IMngr[] = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', mngr, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
  async saveMngr(newMngr: IMngrSnapshot): Promise<TSaveMngrResult> {
    try {
      const hasSid = !!newMngr.mngrSid;
      const url = hasSid ? `/mngrInfo/${newMngr.mngrSid}` : '/mngrInfo';
      const method = hasSid ? this.api.apisauce.put : this.api.apisauce.post;
      const response: ApiResponse<any> = await method(url, newMngr);
      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }
      const mngr: IMngr = response.data.data;
      const responseInfo = response.data;
      return { kind: 'ok', mngr, responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }

  async deleteMngr(mngrSid: number): Promise<TDeleteServiceHistResult> {
    try {
      const url = `/mngrInfo/${mngrSid}`;
      const response: ApiResponse<any> = await this.api.apisauce.delete(url);
      if (!response.ok) {
        const problem = getGeneralApiProblem(response);
        if (problem) return problem;
      }

      const responseInfo = response.data;
      return { kind: 'ok', responseInfo };
    } catch (e) {
      return { kind: 'bad-data' };
    }
  }
}
